import { useEffect, useState, useMemo, useRef } from "react";
import { Form, Container, Nav, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import SimpleTable from "../../components/atoms/table/simpleTable/simpleTable";
import deleteIcon from "../../components/Assests/Delete-Icon.png";
import Edit from "../../components/Assests/Icons/Edit.png";
import CloseIcon from "../../components/Assests/Close-Icon.png";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getUsers, deleteUser, approveUser, hideUser, addUser, updateUser, getAllRoles, getLocations } from "./UserManagementApi";
import "../../components/organisms/SecondTabNavigation.css";
import "../../styles/typography.css";
import "./UserManagement.css";
import Button from "../../components/atoms/button/Button";
import ReusableModal from "../../components/atoms/modal/ReusableModal";
import DynamicForm from "../../components/atoms/dynamicForm/DynamicForm";
import PageDescriptionBar from "../../components/atoms/pageDescriptionBar/PageDescriptionBar";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [paginationData, setPaginationData] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [isITAdmin, setIsITAdmin] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editMode, setEditMode] = useState(null); // null | "add" | "edit"
  const [userFormData, setUserFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    roles: [],
    locations: [],
    customerId: undefined
  });
  console.log("User form data:", userFormData);
  const formRef = useRef(null);

  const tabConfig = [{ label: "User Management", key: "user-Management" }];

  const userFormFields = [
    {
      name: "firstName",
      label: "First Name",
      type: "text",
      validations: ["required"],
      placeholder: "Enter first name",
      disabled: editMode === "edit"     
    },
    {
      name: "lastName",
      label: "Last Name",
      type: "text",
      validations: ["required"],
      placeholder: "Enter last name",
      disabled: editMode === "edit"      
    },
    {
      name: "email",
      label: "Email",
      type: "email",
      validations: ["required", "email"],
      placeholder: "Enter email address",
      disabled: editMode === "edit"
    },
    {
      name: "roles",
      label: "Role",
      type: "multiselect",
      validations: ["required"],
      options: roles,
      placeholder: "Select role"      
    },

    {
      name: "locations",
      label: "Location",
      type: "multiselect",
      validations: ["required"],
      options: locations,
      placeholder: "Select location"      
    }
  ];

  useEffect(() => {
    try {
      const roleFromStorage =
        localStorage.getItem("currentRole") ||
        sessionStorage.getItem("currentRole");

      const parsedRole = roleFromStorage ? JSON.parse(roleFromStorage) : "";
      setIsITAdmin(parsedRole === "IT Admin");
    } catch (error) {
      console.error("Error parsing currentRole from storage:", error);
      setIsITAdmin(false);
    }
  }, []);

  const headerColumns = useMemo(() => {
    const baseColumns = ["Employee Name", "Role", "Location", "Email ID"];

    baseColumns.push("Action");
    if (isITAdmin) {
      baseColumns.push("Hide");
    }
    return baseColumns;
  }, [isITAdmin]);

  const fetchUsers = async (searchText = "", page = 1, pageSize = 25) => {
    try {
      const data = await getUsers(page, pageSize, searchText);
      if (data.success) {
        const { totalRecords, pageNumber, pageSize, totalPages } = data.results;
        setUsers(data.results.results);
        setPaginationData({ totalRecords, pageNumber, pageSize, totalPages });
        setTotalCount(data.totalCount);
      } else {
        console.error("Failed to fetch users");
        setUsers([]);
        setPaginationData({});
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setUsers([]);
      setTotalCount(0);
    }
  };

  const fetchRoles = async () => {
    try {
      const result = await getAllRoles();
      if (result.success) {
        const roleOptions = result.data
          .filter(role => {
            const roleName = role.name || role.roleName || "";
            return roleName.toLowerCase() !== "customer";
          })
          .map(role => ({
            // prefer numeric id if available, fall back to name
            value: role.id || role.roleId || role.name || role.roleName || role,
            label: role.name || role.roleName || role
          }));
        setRoles(roleOptions);
      } else {
        console.error("Failed to fetch roles");
        setRoles([]);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoles([]);
    }
  };

  const fetchLocations = async () => {
    try {
      const result = await getLocations();
      if (result.success) {
        const locationOptions = result.results.map(location => ({
          value: location.locationId || location.id || location,
          label: location.name || location.locationName || location
        }));
        setLocations(locationOptions);
      } else {
        console.error("Failed to fetch locations");
        setLocations([]);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocations([]);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchLocations();
  }, []);

  useEffect(() => {
    // always fetch immediately when criteria change
    fetchUsers(searchTerm, currentPage, rowsPerPage);

    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== "") {
        setCurrentPage(1);
      }
      fetchUsers(searchTerm, currentPage, rowsPerPage);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, rowsPerPage, searchTerm]);

  const handleToggleUser = async (email, currentActive) => {
    try {
      const result = await approveUser(email, !currentActive);
      if (result.success) {
        toast.success(`User status updated successfully`);
        await fetchUsers(searchTerm, currentPage, rowsPerPage);
      } else {
        toast.error("Failed to update user status");
      }
    } catch (error) {
      console.error("Approve user error:", error);
      toast.error("Failed to update user status");
    }
  };

  const handleToggleITAdmin = async (email, currentITAdmin) => {
    try {
      const result = await hideUser(email, !currentITAdmin);
      if (result.success) {
        toast.success(`IT Admin status updated successfully`);
        await fetchUsers(searchTerm, currentPage, rowsPerPage);
      } else {
        toast.error("Failed to update IT Admin status");
      }
    } catch (error) {
      console.error("IT Admin toggle error:", error);
      toast.error("Failed to update IT Admin status");
    }
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    try {
      const deleteResult = await deleteUser(userToDelete.email);
      if (deleteResult.success) {
        await fetchUsers(searchTerm, currentPage, rowsPerPage);
        toast.success("User deleted successfully");
      } else {
        toast.error("Failed to delete user");
      }
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteConfirm(false);
    setUserToDelete(null);
  };

  const resolveMultiSelectIds = (values, options = []) => {
    if (!Array.isArray(values)) return [];

    return values
      .map((item) => {
        if (typeof item === "number") return item;

        if (typeof item === "string") {
          const trimmed = item.trim();
          if (!trimmed) return null;

          const asNumber = Number(trimmed);
          if (!Number.isNaN(asNumber)) return asNumber;

          const matchedOption = options.find(
            (opt) =>
              String(opt.label).toLowerCase() === trimmed.toLowerCase() ||
              String(opt.value).toLowerCase() === trimmed.toLowerCase()
          );

          if (!matchedOption) return null;
          const matchedAsNumber = Number(matchedOption.value);
          return Number.isNaN(matchedAsNumber) ? null : matchedAsNumber;
        }

        if (item && typeof item === "object") {
          const rawValue =
            item.value ?? item.id ?? item.roleId ?? item.locationId ?? item.name ?? item.label;
          const asNumber = Number(rawValue);
          return Number.isNaN(asNumber) ? null : asNumber;
        }

        return null;
      })
      .filter((value) => value !== null);
  };

  const handleAddUser = async (formData) => {
    try {
      // build payload expected by backend
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        // Resolve ids from either numeric values or text labels.
        roles: resolveMultiSelectIds(formData.roles, roles),
        locations: resolveMultiSelectIds(formData.locations, locations)
        ,
        // include customerId when available (some backends require it)
        ...(formData.customerId ? { customerId: formData.customerId } : {})
      };

      console.log("Adding user with payload:", payload);
      const result = await addUser(payload);
      if (result.success) {
        toast.success("User added successfully");
        setShowUserForm(false);
        setEditMode(null);
        setUserFormData({
          firstName: "",
          lastName: "",
          email: "",
            roles: [],
            locations: [],
            customerId: undefined
        });
        await fetchUsers(searchTerm, currentPage, rowsPerPage);
      } else {
        // backend returned an error, it will be on result.error
        let msg = "Failed to add user";
        if (result.error) {
          // axios error object contains response.data which holds the string
          const err = result.error;
          if (err.response && err.response.data) {
            if (typeof err.response.data === 'string') {
              msg = err.response.data;
            } else if (err.response.data.message) {
              msg = err.response.data.message;
            }
          } else if (err.message) {
            msg = err.message;
          }
        }
        console.log("Add user failure message:", msg);
        toast.error(msg);
      }
    } catch (error) {
      console.error("Add user error:", error);
      console.error("Error response:", error.response);
      console.error("Error response data:", error.response?.data);
      
      // Extract specific error message from backend response (mostly plain string)
      let errorMessage = "Failed to add user";
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.log("Final error message to display:", errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleUpdateUser = async (formData) => {
    try {
      const resolvedRoles = resolveMultiSelectIds(formData.roles, roles);
      const resolvedLocations = resolveMultiSelectIds(formData.locations, locations);

      const payload = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        roles: resolvedRoles,
        locations: resolvedLocations,
        customerId: formData.customerId
      };

      console.log("Updating user with payload:", payload);
      const result = await updateUser(payload);
      if (result.success) {
        toast.success("User updated successfully");
        setShowUserForm(false);
        setEditMode(null);
        setUserFormData({
          firstName: "",
          lastName: "",
          email: "",
          roles: [],
          locations: [],
          customerId: undefined
        });
        await fetchUsers(searchTerm, currentPage, rowsPerPage);
      } else {
        let msg = "Failed to update user";
        const err = result.error;
        if (err?.response?.data) {
          if (typeof err.response.data === "string") {
            msg = err.response.data;
          } else if (err.response.data.message) {
            msg = err.response.data.message;
          }
        } else if (err?.message) {
          msg = err.message;
        }
        toast.error(msg);
      }
    } catch (error) {
      console.error("Update user error:", error);
      let msg = "Failed to update user";
      if (error?.response?.data) {
        if (typeof error.response.data === "string") {
          msg = error.response.data;
        } else if (error.response.data.message) {
          msg = error.response.data.message;
        }
      } else if (error?.message) {
        msg = error.message;
      }
      toast.error(msg);
    }
  };

  const handleEditClick = (user) => {
    setEditMode("edit");

    console.log("Editing user:", user);

    // Extract role IDs - try multiple possible formats
    let roleIds = [];
    if (Array.isArray(user.roles)) {
      roleIds = user.roles.map(r => {
        if (typeof r === 'object') {
          return r.value || r.id || r.roleId || r;
        }
        return Number(r) || r;
      });
    } else if (user.roleIds && Array.isArray(user.roleIds)) {
      roleIds = user.roleIds.map(r => Number(r));
    } else if (user.roleId) {
      roleIds = [Number(user.roleId)];
    } else if (user.role && typeof user.role === 'object') {
      roleIds = [user.role.id || user.role.roleId || user.role];
    } else if (user.roleName) {
      const roleNames = Array.isArray(user.roleName)
        ? user.roleName
        : String(user.roleName)
            .split(",")
            .map((name) => name.trim())
            .filter(Boolean);
      roleIds = resolveMultiSelectIds(roleNames, roles);
    }

    // Extract location IDs - try multiple possible formats
    let locationIds = [];
    if (Array.isArray(user.locations)) {
      locationIds = user.locations.map(l => {
        if (typeof l === 'object') {
          return l.value || l.id || l.locationId || l;
        }
        return Number(l) || l;
      });
    } else if (user.locationIds && Array.isArray(user.locationIds)) {
      locationIds = user.locationIds.map(l => Number(l));
    } else if (user.locationId) {
      locationIds = [Number(user.locationId)];
    } else if (user.location && typeof user.location === 'object') {
      locationIds = [user.location.id || user.location.locationId || user.location];
    } else if (user.locationName) {
      const locationNames = Array.isArray(user.locationName)
        ? user.locationName
        : String(user.locationName)
            .split(",")
            .map((name) => name.trim())
            .filter(Boolean);
      locationIds = resolveMultiSelectIds(locationNames, locations);
    }

    console.log("Extracted role IDs:", roleIds, "location IDs:", locationIds);

    setUserFormData({
      firstName: user.firstName || user.first_name || "",
      lastName: user.lastName || user.last_name || "",
      email: user.email || "",
      roles: roleIds,
      locations: locationIds,
      customerId: user.customerId
    });
    setShowUserForm(true);
  };

  const handleCancelAddUser = () => {
    setShowUserForm(false);
    setEditMode(null);
    setUserFormData({
      firstName: "",
      lastName: "",
      email: "",
      roles: [],
      locations: []
    });
  };

  const formatTableData = () =>
    users.map((user) => {
      const rowData = {
        "Employee Name": user.customerName,
        Role: user.roleName ? user.roleName : "Role not Found",
        Location: user.locationName || user.location || "-",
        "Email ID": user.email,
      };

      if (isITAdmin) {
        rowData["Hide"] = (
      <OverlayTrigger placement="top" overlay={<Tooltip>{user.isVisible ? "Hide user" : "Show user"}</Tooltip>}>
      <Form.Check
            type="switch"
            id={`it-admin-switch-${user.email}`}
            checked={!user.isVisible}
            onChange={() => handleToggleITAdmin(user.email, user.isVisible)}
            className="status-switch"
          />
      </OverlayTrigger>
        );
      }

      rowData.Action = (
        <div className="d-flex align-items-center justify-content-center gap-2">
          <OverlayTrigger placement="top" overlay={<Tooltip>{user.isActive ? "Deactivate user" : "Activate user"}</Tooltip>}>
          <Form.Check
            type="switch"
            id={`switch-${user.email}`}
            checked={!!user.isActive}
            onChange={() => handleToggleUser(user.email, user.isActive)}
            className="status-switch"
          />
          </OverlayTrigger>
          <button
            onClick={() => handleDeleteUser(user)}
            className="delete-btn"
            title="Delete user"
          >
            <img src={deleteIcon} alt="Delete" className="delete-icon" />
          </button>

          {isITAdmin && (
            <button
              size="sm"
              variant="edit-active"
              style={{
                padding: 0,
                background: "transparent",
                border: "none",
                marginRight: 20,
              }}
              onClick={() => handleEditClick(user)}
              title="Edit user"
            >
              <img src={Edit} alt="Edit" width={24} height={24} />
            </button>
          )}
        </div>
      );
      return rowData;
    });

  const paginatedData = formatTableData();
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const renderRow = (row, index) => (
    <tr key={index}>
      {headerColumns.map((column, colIndex) => (
        <td key={colIndex}>{row[column]}</td>
      ))}
    </tr>
  );

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} />
      {showDeleteConfirm && (
        <div className="modal-confirm-overlay">
          <div className="modal-confirm-dialog">
            <div className="modal-confirm-header">
              <span className="modal-confirm-title">Confirm</span>
              <button
                className="modal-confirm-close"
                onClick={cancelDeleteUser}
                title="Close"
              >
                <img src={CloseIcon} alt="Close" className="close-icon" />
              </button>
            </div>
            <div className="modal-confirm-body">
              Are you sure you want to delete this user?
            </div>
            <div className="modal-confirm-footer">
              <button
                className="modal-confirm-btn modal-confirm-cancel"
                onClick={cancelDeleteUser}
                title="Cancel delete"
              >
                Cancel
              </button>
              <button className="modal-confirm-btn" onClick={confirmDeleteUser} title="Confirm delete">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ReusableModal
        show={showUserForm}
        onHide={handleCancelAddUser}
        title={editMode === "edit" ? "Edit User" : "Add New User"}
        size="lg"
        buttons={[
          {
            text: "Cancel",
            variant: "outlined",
            onClick: handleCancelAddUser
          },
          {
            text: editMode === "edit" ? "Update" : "Add",
            variant: "active",
            onClick: () => {
              if (formRef.current) {
                const isValid = formRef.current.validate();
                if (isValid) {
                  const formData = formRef.current.getData();
                  // enforce roles and locations selection
                  if (!formData.roles || formData.roles.length === 0) {
                    toast.error("Please select at least one Role.");
                    return;
                  }
                  if (!formData.locations || formData.locations.length === 0) {
                    toast.error("Please select at least one Location.");
                    return;
                  }
                  if (editMode === "edit") {
                    handleUpdateUser(formData);
                  } else {
                    handleAddUser(formData);
                  }
                }
              }
            }
          }
        ]}
      >
        <DynamicForm
          ref={formRef}
          fields={userFormFields}
          initialData={userFormData}
          onSubmit={handleAddUser}
          onChange={setUserFormData}
        />
      </ReusableModal>

      <Container fluid className="second-tab-container">
        <Nav
          variant="underline"
          activeKey="user-details"
          className="flex-nowrap nav-underline"
        >
          {tabConfig.map((tab) => (
            <Nav.Item key={tab.key}>
              <Nav.Link
                eventKey={tab.key}
                className="default-font-styles"
                active
              >
                {tab.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </Container>

      <div className="tab-content-container p-3">
        <div className="user-details-content">

          <PageDescriptionBar items={[
            { type: "badge", badge: "active",   text: "Active – User is enabled and can log in" },
            { type: "badge", badge: "inactive", text: "Inactive – User is disabled and cannot log in" },
            { type: "separator" },
            { type: "img",   src: deleteIcon,   text: "Delete – Permanently removes the user" },
            { type: "icon",  icon: "✏️", text: "Edit – Modify user roles & locations", note: "IT Admin only" },
            { type: "icon",  icon: "👁", text: "Hide – Toggle user visibility in the system", note: "IT Admin only" },
          ]} />

          <Row className="align-items-center mb-4 search-filter-bar">
            
            <Col />
                     
            <Col xs="auto" className="d-flex align-items-center gap-2">
              {isITAdmin && (
                <div className="d-flex align-items-center justify-content-end">
                  <Button
                    size="md"
                    variant="outlined"
                    title="Add User"
                    onClick={() => {
                      setEditMode("add");
                      setUserFormData({
                        firstName: "",
                        lastName: "",
                        email: "",
                        roles: [],
                        locations: []
                      });
                      setShowUserForm(true);
                    }}
                  >
                    Add User
                  </Button>
                </div>
              )}
              <Form.Control
                type="text"
                placeholder="Search"
                title="Search users"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </Col>
          </Row>

          <div className="users-table-section mb-4">
            <div className="bg-white rounded shadow-sm p-4">
              <SimpleTable
                columns={headerColumns}
                data={paginatedData}
                customRowRender={renderRow}
                showPagination
                paginationData={paginationData}
                searchValue={searchTerm}
                fetchData={fetchUsers}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
